/**
 * shopifyAdmin — EIN Ort fuer die Shopify-Admin-Adressen des Frontends.
 *
 * @description
 *   Die Zeile `const SHOPIFY_ADMIN = (process.env.REACT_APP_SHOPIFY_ADMIN_URL ||
 *   'https://admin.shopify.com/store/kaffeemanuale').replace(/\/+$/, '')` steht
 *   sechsfach im Haus (`rma/RMADetail.jsx`, `rma/components/SituationHeader.jsx`,
 *   `rma/components/RefundAndResolveDialog.jsx`, `rma/components/ShopifyCaseBlock.jsx`,
 *   `pages/secondLife/bestand.js`, `rma/timeline/toActivityEntry.js`) — und
 *   `ProductDetail.jsx` liest fuer dieselbe Sache `REACT_APP_SHOPIFY_STORE`.
 *   Das ist als Idee abgelegt (`docs/ideas/2026-10-04-shopify-admin-konstante-sechsfach.md`,
 *   P4, offen). Diese Datei legt die EINE Stelle an, die die Idee verlangt;
 *   die sechs Aufrufer stellt sie **nicht** um — das ist die Idee selbst.
 *
 *   🔴 Der Rueckfall traegt den Shop-Namen. Wechselt der Shop, sind sonst sechs
 *   Stellen zu aendern, und die sechste wird vergessen — der Fehler waere still:
 *   der Link oeffnet einen fremden Shop, ohne Fehlermeldung.
 *
 * @useWhen
 *   - Ein Kunde, eine Bestellung oder ein Produkt soll im Shopify-Admin
 *     geoeffnet werden (neuer Tab)
 *   - Eine Flaeche braucht die Admin-Basis als Zeichenkette
 *
 * @dontUseFor
 *   - Kundenlinks IM Shop (Storefront) — das ist nicht `admin.shopify.com`
 *   - Backend-Links: `mailers.py::_shopify_admin_base()` ist die Serverform und
 *     faellt ohne `REACT_APP_SHOPIFY_STORE` auf `https://admin.shopify.com`
 *     **ohne Shop** zurueck (toter Link). Hier gilt die Frontend-Form.
 *
 * @related
 *   - `ProductDetail.jsx` — die aeltere Einzelkopie mit `REACT_APP_SHOPIFY_STORE`
 *
 * @reuseHints
 *   - Die sechs Kopien nach und nach hierher ziehen (Idee vom 2026-10-04)
 */

/** Der Shop-Name, wenn keine Umgebungsvariable gesetzt ist — wie im Haus. */
const STORE_FALLBACK = 'kaffeemanuale'

/**
 * Die Admin-Basis, ohne Schraegstrich am Ende.
 *
 * 🔴 Zwei Variablen, ein Vorrang, und beide werden **in der Funktion** gelesen
 * (nicht als Modulkonstante): so ist der Rueckfall in einer Jest-Probe
 * umstellbar, ohne das Modul neu zu laden.
 *
 * @returns {string} z. B. `https://admin.shopify.com/store/kaffeemanuale`
 */
export function shopifyAdminBase() {
  const url = process.env.REACT_APP_SHOPIFY_ADMIN_URL
  if (url) return String(url).replace(/\/+$/, '')
  const store = process.env.REACT_APP_SHOPIFY_STORE || STORE_FALLBACK
  return `https://admin.shopify.com/store/${store}`
}

/**
 * Der Admin-Link eines Kunden.
 *
 * @param {string|number|null|undefined} shopifyCustomerId die numerische
 *   Shopify-Kundennummer. 🔴 **Ohne Kennung gibt es KEINEN Link** — `null`
 *   zurueck, und die Oberflaeche nennt den Grund, statt einen toten Link zu
 *   zeigen (`…/customers/undefined` waere einer).
 * @returns {string|null}
 */
export function shopifyCustomerUrl(shopifyCustomerId) {
  if (!shopifyCustomerId) return null
  return `${shopifyAdminBase()}/customers/${shopifyCustomerId}`
}

/**
 * Der Admin-Link einer Bestellung.
 *
 * @param {string|number|null|undefined} orderId `shp_orders.order_id` — der
 *   **numerische** String (Migration 204:66-67), nie eine `gid://…`-Form.
 * @returns {string|null}
 */
export function shopifyOrderUrl(orderId) {
  if (!orderId) return null
  return `${shopifyAdminBase()}/orders/${orderId}`
}

/**
 * Der Zahlenteil einer Shopify-GID, fuer die Anzeige.
 *
 * Shopify liefert Kennungen in zwei Formen: nackt numerisch (`shp_orders.order_id`,
 * Migration 204:66-67) und als GID (`gid://shopify/Order/13488482681209`). In einer
 * Anzeige taugt nur der Zahlenteil; die GID ist keine Kennung, mit der jemand
 * arbeitet.
 *
 * 🔴 Der Vertrag ist ENG, und zwar gegen `utils/variantKey.js:25-29`: dessen
 * Zerlegung nimmt JEDE Zeichenkette mit `/` (`a/b` → `b`) und liefert bei
 * abschliessendem `/` den leeren String — als Rueckfall in einer Anzeige waere
 * das ein leerer Text. Hier wird ausschliesslich
 * `^gid://shopify/(Order|LineItem)/<Zahl>$` zerlegt; jede andere Eingabe kommt
 * UNVERAENDERT zurueck (`null`, `undefined`, reine Zahl, fremder String), und
 * fuer eine nicht-leere Eingabe ist das Ergebnis nie leer.
 *
 * Die zwei bestehenden Inline-Kopien derselben Zeile bleiben stehen und werden
 * hier NICHT umgebaut: `fall/lib/ware.js:67` (`bestellLink`) und
 * `rma/RMADetail.jsx:2398` (Admin-Link).
 *
 * @param {string|number|null|undefined} value
 * @returns {string|number|null|undefined} Zahlenteil als String, sonst `value`
 */
export function shopifyNumericId(value) {
  if (value === null || value === undefined) return value
  const match = /^gid:\/\/shopify\/(?:Order|LineItem)\/(\d+)$/.exec(String(value))
  return match ? match[1] : value
}
